// A JEGY ÁLLAPOT-VEZÉRLŐI A FEJLÉCBEN — státusz (kattintható badge) · felelős · support-csapat.
//
// MIÉRT NINCS TÖBBÉ „Kezelés" SZEKCIÓ (user-döntés): a három beállítás elférhet a fejléc-sorban, a
// státusz pedig eleve badge-ként áll ott — így a jelölő MAGA a vezérlő. Ez az iparági minta (a support-eszközök
// státusz-chipje kattintható), és megszünteti a duplikációt: eddig a státusz KÉTSZER látszott (badge a fejlécben
// + select a szekcióban), a felelős/csapat pedig egy külön blokkot igényelt.
//
// ══ MENTÉS: AZONNAL, VEZÉRLŐNKÉNT ══════════════════════════════════════════════════════════════════════════
// Nincs „Mentés" gomb: minden változás SAJÁT PATCH-et indít (optimista toast, OCC `If-Match` a betöltött
// verzióval). Ez a beágyazott vezérlők bevett viselkedése — egy fejléc-menüben ülő „Mentés" félrevezető lenne.
// A contract `status` + `closeReason` mezőt MINDIG kér, ezért minden patch a JELENLEGI értékeket viszi, és csak
// a változtatott mező más (parciális szemantika a hívó szintjén).
//
// ══ LEZÁRÁS = DIALÓGUS ═════════════════════════════════════════════════════════════════════════════════════
// A `closed` státusz nem állítható egy menü-kattintással: a lezárás-ok KÖTELEZŐ (a BFF 422-zik nélküle), és a
// „partner is megkapja" döntés is ehhez a pillanathoz tartozik. Ezért a menüben a „Lezárva" egy DIALÓGUST nyit
// (ok + megosztás-pipa) — ez egyben jobb UX is, mint a korábbi, szekcióban megjelenő rejtett textarea.
//
// Jog nélkül (`canManage === false`) mindhárom vezérlő SIMA KIJELZÉS (badge + szöveg), menü nélkül; a lezárás-ok
// a jegy alatt olvasható marad (a hívó lap rendereli).
import { type TicketDetailSchema } from '@atlas/contracts';
import { TICKET_STATUSES, type TicketStatus } from '@atlas/shared';
import {
  ChevronDownIcon,
  LifebuoyIcon,
  LockClosedIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { forwardRef, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { type z } from 'zod';

import { EntityCombobox } from '@/components/shared/entity-combobox';
import { PersonChip } from '@/components/shared/person-chip';
import { TicketStatusBadge } from '@/components/tickets/ticket-status-badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import { ticketKeys } from '@/features/tickets/api';
import { teamForAssignee } from '@/features/tickets/assignee-team';
import { api } from '@/lib/api';
import { toPersonOptions } from '@/lib/person-options';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { cn } from '@/lib/utils';

type TicketDetail = z.infer<typeof TicketDetailSchema>;
const STATUS_OPTIONS = TICKET_STATUSES;

/** A patch alakja — a contract `status`/`closeReason` mezőt MINDIG kér (lásd a fájl-fejlécet). */
interface PatchInput {
  status: TicketStatus;
  closeReason: string | null;
  shareCloseReason?: boolean | undefined;
  assigneeId?: string | null | undefined;
  supportTeamId?: string | null | undefined;
}

export interface TicketInlineControlsProps {
  ticket: TicketDetail;
  /** Kezelés joga (tickets.manage) — enélkül mindhárom vezérlő sima kijelzés. */
  canManage: boolean;
}

/**
 * Fejléc-vezérlő gomb (felelős/csapat) — halvány, kompakt, a badge-ekkel egy sorban olvasható.
 *
 * `forwardRef` + `...rest`: a `DropdownMenuTrigger asChild` a GYEREK-ELEMRE adja rá a nyitó kezelőket
 * (`onPointerDown`/`onKeyDown`/`aria-expanded`) és a ref-et. Egy sima függvény-komponens, ami ezeket
 * eldobja, NÉMÁN nem nyíló menüt csinál (így is volt: a felelős/csapat menü nem nyílt meg).
 */
const ControlButton = forwardRef<
  HTMLButtonElement,
  {
    label: string;
    icon: React.ReactNode;
    children: React.ReactNode;
    disabled?: boolean;
  } & React.ButtonHTMLAttributes<HTMLButtonElement>
>(function ControlButton({ label, icon, children, disabled, ...rest }, ref): JSX.Element {
  return (
    <button
      {...rest}
      ref={ref}
      type="button"
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        'ease-atlas focus-visible:ring-accent-600 inline-flex min-w-0 items-center gap-1.5 rounded-md px-1.5 py-0.5 text-sm transition-colors duration-150',
        'text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2',
        'disabled:pointer-events-none',
      )}
    >
      {icon}
      <span className="min-w-0 truncate">{children}</span>
      <ChevronDownIcon className="size-3 shrink-0 opacity-60" />
    </button>
  );
});

export function TicketInlineControls({
  ticket,
  canManage,
}: TicketInlineControlsProps): JSX.Element {
  const { t, i18n } = useTranslation();

  // Opció-listák (felelős-jelöltek + csapatok) — kis, cache-elt lista; CSAK kezelési joggal kérjük.
  const refsQ = api.ticketRefs.useQuery(
    ['ticket-refs'],
    {},
    { queryKey: ['ticket-refs'], enabled: canManage },
  );
  const refs = refsQ.data?.status === 200 ? refsQ.data.body : undefined;

  /** A lezárás-dialógus állapota (ok + megosztás-pipa). */
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeReason, setCloseReason] = useState(ticket.closeReason ?? '');
  const [shareCloseReason, setShareCloseReason] = useState(false);
  const shareId = useId();

  // A szerver-állapot változásakor (mentés → új version) a dialógus-piszkozat visszaáll a mentettre.
  useEffect(() => {
    setCloseReason(ticket.closeReason ?? '');
    setShareCloseReason(false);
  }, [ticket.id, ticket.version, ticket.closeReason]);

  const status = (STATUS_OPTIONS as readonly string[]).includes(ticket.status)
    ? (ticket.status as TicketStatus)
    : 'open';

  const detailKey = ticketKeys.detail(ticket.id);
  const mutation = useEntityMutation<PatchInput>({
    mutationFn: (v) =>
      api.ticketUpdate.mutation({
        params: { id: ticket.id },
        body: {
          status: v.status,
          closeReason: v.closeReason,
          ...(v.shareCloseReason === undefined ? {} : { shareCloseReason: v.shareCloseReason }),
          ...(v.assigneeId === undefined ? {} : { assigneeId: v.assigneeId }),
          ...(v.supportTeamId === undefined ? {} : { supportTeamId: v.supportTeamId }),
        },
        headers: { 'if-match': String(ticket.version) },
      }),
    invalidateKeys: [detailKey, ticketKeys.lists()],
    onConflict: () => {
      void api.ticketGet.query({ params: { id: ticket.id } });
    },
    messages: {
      success: t('toast:tickets.updated'),
      conflict: t('toast:tickets.conflict'),
      forbidden: t('toast:tickets.forbidden'),
      validation: t('toast:tickets.validation'),
      error: t('toast:tickets.error'),
    },
  });

  /** A JELENLEGI állapot + a változtatott mező (a contract mindig kér státuszt és lezárás-okot). */
  const patch = (change: Partial<PatchInput>): void => {
    mutation.mutate({ status, closeReason: ticket.closeReason ?? null, ...change });
  };

  /**
   * FELELŐS-váltás: a kolléga csapata EGY mentésben áll be vele (Évi #83, `teamForAssignee`). A „Nincs felelős" a
   * csapathoz nem nyúl; több csapat tagjánál a csapat marad, és egy rövid jelzés mondja meg, miért.
   */
  const onAssigneeChange = (id: string | null): void => {
    if (id === null) {
      patch({ assigneeId: null });
      return;
    }
    const decision = teamForAssignee(id, ticket.supportTeamId, refs?.supportTeams ?? []);
    patch(
      decision.kind === 'set'
        ? { assigneeId: id, supportTeamId: decision.teamId }
        : { assigneeId: id },
    );
    if (decision.kind === 'ambiguous') toast.info(t('toast:tickets.assigneeTeamAmbiguous'));
  };

  /**
   * A jegy MÁR lezárt volt? Ekkor a dialógus nem LEZÁR, hanem a lezárás-okot SZERKESZTI — és a megosztás-pipa
   * tiltott: a BFF (helyesen) csak a lezárás ÁTMENETÉN küld `ticket.closed` értesítést, tehát egy már lezárt
   * jegynél a pipa NO-OP lenne (a user siker-toastot kapna, a partner semmit).
   */
  const alreadyClosed = status === 'closed';

  /**
   * A státusz-menü választása. A `closed` a lezárás-DIALÓGUST nyitja (ok + megosztás), nem ír azonnal — és MÁR
   * LEZÁRT jegynél is nyílik: így marad út a lezárás-ok utólagos szerkesztésére (a kezelő-szekció textareáját
   * ez váltotta le). Minden más státusz azonnal ír.
   */
  const onPickStatus = (next: TicketStatus): void => {
    if (next === 'closed') {
      setCloseOpen(true);
      return;
    }
    if (next === status) return;
    patch({ status: next });
  };

  const statusBadge = (
    <TicketStatusBadge
      status={status}
      label={t(`tickets.statusLabel.${status}`, { defaultValue: status })}
    />
  );

  // ── JOG NÉLKÜL: sima kijelzés (badge + szövegek), menü nélkül ──
  if (!canManage) {
    return (
      <>
        {statusBadge}
        <span className="text-muted-foreground inline-flex min-w-0 items-center gap-1.5 text-sm">
          <UserIcon className="size-3.5 shrink-0" aria-hidden />
          {/* Feloldatlan, de LÉTEZŐ felelős-id = törölt személy — a „Nincs felelős" itt hazudna. */}
          {ticket.assigneeName ??
            (ticket.assigneeId ? t('common.deletedUser') : t('tickets.unassigned'))}
        </span>
        <span className="text-muted-foreground inline-flex min-w-0 items-center gap-1.5 text-sm">
          <LifebuoyIcon className="size-3.5 shrink-0" aria-hidden />
          {ticket.supportTeamName ?? t('tickets.fields.noTeam')}
        </span>
      </>
    );
  }

  return (
    <>
      {/* STÁTUSZ — a badge MAGA a menü-trigger (a jelölő és a vezérlő egy). */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t('tickets.fields.status')}
            title={t('tickets.fields.status')}
            disabled={mutation.isPending}
            className="ease-atlas focus-visible:ring-accent-600 inline-flex items-center gap-1 rounded-full transition-opacity duration-150 hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 disabled:pointer-events-none"
          >
            {statusBadge}
            <ChevronDownIcon className="text-muted-foreground size-3 shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {STATUS_OPTIONS.map((s) => (
            <DropdownMenuItem
              key={s}
              onSelect={() => onPickStatus(s)}
              className={cn('flex-col items-start gap-0.5', s === status && 'bg-accent')}
              // a felolvasott NÉV a státusz, a súgó leírásként megy (nem olvad a névbe)
              aria-label={t(`tickets.statusLabel.${s}`, { defaultValue: s })}
              aria-description={t(`tickets.statusHelp.${s}`)}
            >
              <span>{t(`tickets.statusLabel.${s}`, { defaultValue: s })}</span>
              {/* a státusz rövid SÚGÓJA (Évi #86) — a Megoldva / Lezárva különbsége így a választáskor látszik */}
              <span aria-hidden className="text-muted-foreground max-w-64 text-xs">
                {t(`tickets.statusHelp.${s}`)}
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* FELELŐS — KERESHETŐ lista (sok munkatárs közül görgetni lassú volt; user-lelet): a közös
          `EntityCombobox` a fejléc saját chip-gombjával nyílik, a „Nincs felelős" a lista első sora */}
      <EntityCombobox
        value={ticket.assigneeId}
        onChange={onAssigneeChange}
        // nyelvhelyes névsorrend + az adott nyelv ABC-rendje (közös `toPersonOptions`)
        options={toPersonOptions(refs?.assignees ?? [], i18n.language).map((a) => ({
          id: a.id,
          label: a.name,
        }))}
        nullOptionLabel={t('tickets.unassigned')}
        disabled={mutation.isPending}
        aria-label={t('tickets.fields.assignee')}
        trigger={
          <ControlButton
            label={t('tickets.fields.assignee')}
            icon={
              ticket.assigneeName !== null ? (
                <PersonChip
                  name={ticket.assigneeName}
                  givenName={ticket.assigneeGivenName}
                  familyName={ticket.assigneeFamilyName}
                  avatarUrl={ticket.assigneeAvatarUrl}
                  accentColor={ticket.assigneeTeamColor}
                  size="sm"
                  className="min-w-0 gap-1.5"
                />
              ) : (
                <UserIcon className="size-3.5 shrink-0" aria-hidden />
              )
            }
            disabled={mutation.isPending}
          >
            {ticket.assigneeName === null
              ? ticket.assigneeId
                ? t('common.deletedUser')
                : t('tickets.unassigned')
              : ''}
          </ControlButton>
        }
      />

      {/* SUPPORT-CSAPAT */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <ControlButton
            label={t('tickets.fields.supportTeam')}
            icon={<LifebuoyIcon className="size-3.5 shrink-0" aria-hidden />}
            disabled={mutation.isPending}
          >
            {ticket.supportTeamName ?? t('tickets.fields.noTeam')}
          </ControlButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
          <DropdownMenuItem onSelect={() => patch({ supportTeamId: null })}>
            {t('tickets.fields.noTeam')}
          </DropdownMenuItem>
          {(refs?.supportTeams ?? []).map((s) => (
            <DropdownMenuItem
              key={s.id}
              onSelect={() => patch({ supportTeamId: s.id })}
              className={cn(s.id === ticket.supportTeamId && 'bg-accent')}
            >
              <span className="truncate">{s.name}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* LEZÁRÁS-DIALÓGUS — a lezárás-ok KÖTELEZŐ, a megosztás-döntés pedig ehhez a pillanathoz tartozik. */}
      <Dialog open={closeOpen} onOpenChange={(o) => !mutation.isPending && setCloseOpen(o)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {alreadyClosed ? t('tickets.close.editTitle') : t('tickets.close.title')}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor={`${shareId}-reason`}>{t('tickets.fields.closeReason')}</Label>
              <Textarea
                id={`${shareId}-reason`}
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                rows={3}
                maxLength={4000}
                placeholder={t('tickets.fields.closeReasonHint')}
              />
            </div>
            {/* Megosztás-pipa — deny-by-default; üres lezárás-ok esetén tiltott (nincs mit megosztani). */}
            <div className="flex items-start gap-3">
              <Checkbox
                id={shareId}
                className="mt-0.5"
                checked={shareCloseReason}
                onCheckedChange={(v) => setShareCloseReason(v === true)}
                // Üres ok → nincs mit megosztani; MÁR lezárt jegy → az értesítés csak az átmenetkor megy.
                disabled={closeReason.trim() === '' || alreadyClosed}
                aria-label={t('tickets.fields.shareCloseReason')}
              />
              <div className="space-y-0.5">
                <Label htmlFor={shareId} className="cursor-pointer">
                  {t('tickets.fields.shareCloseReason')}
                </Label>
                <p className="text-subtle text-xs">
                  {alreadyClosed
                    ? t('tickets.fields.shareCloseReasonClosedHint')
                    : t('tickets.fields.shareCloseReasonHint')}
                </p>
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setCloseOpen(false)}
              disabled={mutation.isPending}
            >
              {t('tickets.cancel')}
            </Button>
            <Button
              type="button"
              variant="brand"
              disabled={closeReason.trim() === '' || mutation.isPending}
              onClick={() =>
                mutation.mutate(
                  {
                    status: 'closed',
                    closeReason: closeReason.trim(),
                    // A megosztás-döntés EXPLICIT boolean (a `false` maga a „ne oszd meg"); már lezárt jegynél
                    // mindig `false`, mert ott az értesítés amúgy sem indul.
                    shareCloseReason:
                      !alreadyClosed && closeReason.trim() !== '' && shareCloseReason,
                  },
                  { onSuccess: () => setCloseOpen(false) },
                )
              }
            >
              {!alreadyClosed && <LockClosedIcon className="size-4" />}
              {mutation.isPending
                ? t('tickets.saving')
                : alreadyClosed
                  ? t('tickets.save')
                  : t('tickets.close.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** A megjelenített (olvasható) lezárás-ok — a hívó lap a fejléc alatt rendereli, ha van. */
export function TicketCloseReason({ ticket }: { ticket: TicketDetail }): JSX.Element | null {
  const { t } = useTranslation();
  if (ticket.closeReason === null || ticket.closeReason === '') return null;
  return (
    <div className="space-y-1">
      <Text variant="overline" as="div">
        {t('tickets.fields.closeReason')}
      </Text>
      <p className="whitespace-pre-line text-sm">{ticket.closeReason}</p>
    </div>
  );
}
